import type {
  HoldingSnapshot,
  PortfolioRefresh,
  PortfolioSnapshot,
  PortfolioSnapshotDraft,
  RoomMood,
} from "./types";
import {
  clampSceneValueV1,
  holdingMoodSignalV1,
  usableSceneMarketValueV1,
} from "./sceneRulesV1";
import {
  SCENE_V2_MAX_SLIME_SCALE,
  SCENE_V2_MIN_SLIME_SCALE,
  slimeScaleSignalV2,
} from "./sceneRulesV2";
import { deriveRoomSignalsV3 } from "./sceneRulesV3";

export const MIN_SLIME_SCALE = SCENE_V2_MIN_SLIME_SCALE;
export const MAX_SLIME_SCALE = SCENE_V2_MAX_SLIME_SCALE;
export const DEFAULT_FARM_UNIT = 1;

export function clamp(value: number, minimum: number, maximum: number): number {
  return clampSceneValueV1(value, minimum, maximum);
}

function safeMarketValue(value: number): number {
  return usableSceneMarketValueV1(value);
}

export function calculateFarmUnit(
  positions: readonly Pick<HoldingSnapshot, "marketValue">[],
): number {
  const values = positions
    .map(({ marketValue }) => safeMarketValue(marketValue))
    .filter((value) => value > 0)
    .sort((left, right) => left - right);

  if (values.length === 0) {
    return DEFAULT_FARM_UNIT;
  }

  const middle = Math.floor(values.length / 2);
  if (values.length % 2 === 1) {
    return values[middle];
  }

  return (values[middle - 1] + values[middle]) / 2;
}

export function calculateAmountRatio(
  marketValue: number,
  farmUnit: number,
): number {
  const safeUnit =
    Number.isFinite(farmUnit) && farmUnit > 0 ? farmUnit : DEFAULT_FARM_UNIT;
  return safeMarketValue(marketValue) / safeUnit;
}

/**
 * A continuous logarithmic mapping. `marketValue === farmUnit` maps to 1.0,
 * while the wider outer range keeps small and large holdings easy to compare.
 */
export function calculateSlimeScale(
  marketValue: number,
  farmUnit: number,
): number {
  const amountRatio = calculateAmountRatio(marketValue, farmUnit);
  return slimeScaleSignalV2(amountRatio);
}

export function calculateMood(dailyReturnPct: number | null): number {
  return holdingMoodSignalV1(dailyReturnPct);
}

export function hasUsableReturn(
  holding: Pick<HoldingSnapshot, "dailyReturnPct" | "quoteStatus">,
): boolean {
  return (
    holding.quoteStatus === "valid" &&
    holding.dailyReturnPct !== null &&
    Number.isFinite(holding.dailyReturnPct)
  );
}

export interface RoomMoodDerivation {
  room: RoomMood;
  totalMarketValue: number;
  weightedDailyReturnPct: number;
}

export function deriveRoomMood(
  positions: readonly HoldingSnapshot[],
): RoomMoodDerivation {
  const signals = deriveRoomSignalsV3(
    positions.map((holding) => ({
      marketValue: holding.marketValue,
      dailyReturnPct: holding.dailyReturnPct,
      returnUsable: hasUsableReturn(holding),
    })),
  );

  const room: RoomMood = {
    valence: signals.valence,
    mood: signals.valence,
    knownReturnPct: signals.knownReturnPct,
    dataCoverage: signals.dataCoverage,
    coverage: signals.dataCoverage,
    partyIntensity: signals.partyIntensity,
    stormIntensity: signals.stormIntensity,
    panicIntensity: signals.panicIntensity,
    weather: signals.weather,
  };

  return {
    room,
    totalMarketValue: signals.totalMarketValue,
    weightedDailyReturnPct: signals.knownReturnPct,
  };
}

function validFarmUnit(value: number | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

/**
 * Initializes a portfolio's money anchor. Pass the previous anchor to retain it
 * across imports; otherwise an explicit draft value or the initial median wins.
 */
export function createPortfolioSnapshot(
  draft: PortfolioSnapshotDraft,
  previousFarmUnit?: number,
): PortfolioSnapshot {
  const farmUnit = validFarmUnit(previousFarmUnit)
    ? previousFarmUnit
    : validFarmUnit(draft.farmUnit)
      ? draft.farmUnit
      : calculateFarmUnit(draft.positions);

  return {
    ...draft,
    farmUnit,
    positions: [...draft.positions],
  };
}

/** Carries the fixed personal anchor forward while quotes and values change. */
export function refreshPortfolioSnapshot(
  previous: PortfolioSnapshot,
  refresh: PortfolioRefresh,
): PortfolioSnapshot {
  return {
    ...previous,
    asOf: refresh.asOf ?? previous.asOf,
    marketDate: refresh.marketDate ?? previous.marketDate,
    positions: [...refresh.positions],
    farmUnit: previous.farmUnit,
  };
}
